"use client"

import React, { useState, useEffect, useRef } from "react"
import { BookOpen, User, Lock, Bug, ClipboardCheck, MessageSquare, Sparkles, ChevronRight, LucideIcon, LayoutDashboard, Users, Cpu, DollarSign, Activity, MonitorPlay, Flame, BarChart3, TrendingUp, UserSearch, Mail, Tag, Ticket, Server, FileText, Shield, ShieldCheck, Briefcase, Bell, CreditCard, Coins, Search, ShoppingBag, Network } from "lucide-react"
import { NavMain } from "./nav-main"
import { NavUser } from "./nav-user"
import { TeamSwitcher } from "./team-switcher"
import { SearchDialog, SearchItem } from "./search-dialog"
import { getLoginAppUrl, getAccountAppUrl, getSidebarUser } from "../lib/auth"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible"
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarRail,
    SidebarSeparator,
    SidebarGroup,
} from "../ui/sidebar"

const FEEDBACK_BASE = "https://feedback.askfinz.ai"
const FEEDBACK_ITEMS = [
    { title: "Issues & Bugs",      path: "/issues",       icon: Bug },
    { title: "Awaiting Verify",    path: "/verify",       icon: ClipboardCheck },
    { title: "Feature Requests",   path: "/requests",     icon: MessageSquare },
    { title: "Shipped & Resolved", path: "/shipped",      icon: Sparkles },
    { title: "Report Issue",       path: "/issues/new",   icon: Bug },
    { title: "New Request",        path: "/requests/new", icon: MessageSquare },
]

// ── askFinz Portal (admin-only) ─────────────────────────────────────────
// The internal admin panel (formerly the "Dash" group in GLOBAL_APPS) now
// lives here in the sidebar footer, gated behind isAdmin and marked with a
// Lock icon. Items deep-link into dash.askfinz.ai.
const PORTAL_BASE = "https://dash.askfinz.ai"
const PORTAL_ITEMS = [
    // Analytics
    { title: "Overview",           path: "/",                        icon: LayoutDashboard },
    { title: "User Analytics",     path: "/users",                   icon: Users },
    { title: "Model & LLM Usage",  path: "/usage/models",            icon: Cpu },
    { title: "Revenue & Costs",    path: "/revenue",                 icon: DollarSign },
    { title: "Service Usage",      path: "/services",                icon: Activity },
    { title: "Session Recordings", path: "/analytics",               icon: MonitorPlay },
    { title: "Heatmap",            path: "/analytics/heatmap",       icon: Flame },
    { title: "Research SLOs",      path: "/research-analytics",      icon: BarChart3 },
    { title: "SEO & Search",       path: "/seo",                     icon: Search },
    // Finance & HR
    { title: "Financials",         path: "/financials",              icon: TrendingUp },
    { title: "Shop Revenue",       path: "/shop-revenue",            icon: ShoppingBag },
    { title: "Acquisition",        path: "/acquisition",             icon: UserSearch },
    { title: "Beta Waitlist",      path: "/beta-waitlist",           icon: Mail },
    { title: "Employees",          path: "/employees",               icon: Users },
    { title: "Pricing",            path: "/pricing",                 icon: Tag },
    { title: "Discounts",          path: "/discounts",               icon: Ticket },
    // Library
    { title: "Library",            path: "/library",                 icon: BookOpen },
    { title: "Library Earnings",   path: "/library/earnings",        icon: DollarSign },
    { title: "Library Buyers",     path: "/library/buyers",          icon: Users },
    { title: "Platform Ledger",    path: "/library/platform-ledger", icon: Activity },
    // Administration
    { title: "System Health",      path: "/system",                  icon: Server },
    { title: "Model Map",          path: "/model-map",               icon: Network },
    { title: "Audit Log",          path: "/audit",                   icon: FileText },
    { title: "Access Control",     path: "/admin",                   icon: Shield },
    { title: "Compliance",         path: "/compliance",              icon: ShieldCheck },
    { title: "Company Info",       path: "/company",                 icon: Briefcase },
]

// ── Account pages ───────────────────────────────────────────────────────
// The footer "Account" entry expands to the account.askfinz.ai surfaces so
// users can jump straight to a specific account page. Base resolved at render
// via getAccountAppUrl(). Mirrors the routes under account.askfinz.ai/app/.
const ACCOUNT_ITEMS = [
    { title: "Overview",          path: "/account",        icon: LayoutDashboard },
    { title: "Profile",           path: "/profile",        icon: User },
    { title: "Security & Access", path: "/security",       icon: Shield },
    { title: "Notifications",     path: "/notifications",  icon: Bell },
    { title: "Billing",           path: "/billing",        icon: CreditCard },
    { title: "Usage",             path: "/usage",          icon: Activity },
    { title: "Credits",           path: "/credits",        icon: Coins },
    { title: "API Keys",          path: "/api-keys",       icon: Lock },
    { title: "Integrations",      path: "/integrations",   icon: Server },
    { title: "Teams",             path: "/teams",          icon: Users },
    { title: "Company",           path: "/company",        icon: Briefcase },
]

export interface NavItem {
    title: string
    url?: string
    icon?: LucideIcon
    isActive?: boolean
    /** Section label this item belongs under (e.g. "Core", "Tools",
     *  "Verticals"). Empty/undefined → no label (rendered ungrouped, e.g. Home).
     *  Consecutive items sharing a category render under one header. */
    category?: string
    items?: {
        title: string
        url: string
        icon?: LucideIcon
        onClick?: () => void
    }[]
    onClick?: () => void
}

export interface TeamData {
    name: string
    company: { name: string }
}

export interface UserData {
    name: string
    email: string
    avatar: string
}

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
    /** Main navigation items */
    navMain?: NavItem[]
    /** Secondary navigation items (e.g., Settings, Help) */
    navSecondary?: NavItem[]
    /** Team/organization data */
    team?: TeamData
    /** User data */
    user?: UserData
    /** Settings dialog component (optional) */
    settingsDialog?: React.ReactNode
    /** Callback when settings is clicked */
    onSettingsClick?: () => void
    /** Search items for CMD+K search */
    searchItems?: SearchItem[]
    /** Show search dialog */
    showSearch?: boolean
    /** Search placeholder */
    searchPlaceholder?: string
    /** Custom search function */
    onSearch?: (query: string) => Promise<SearchItem[]> | SearchItem[]
}

export function AppSidebar({
    navMain = [],
    navSecondary = [],
    team,
    user = {
        name: "User",
        email: "user@example.com",
        avatar: "",
    },
    settingsDialog,
    onSettingsClick,
    searchItems = [],
    showSearch = false,
    searchPlaceholder = "Search",
    onSearch,
    ...props
}: AppSidebarProps) {
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [feedbackOpen, setFeedbackOpen] = useState(false)
    const [portalOpen, setPortalOpen] = useState(false)
    const [accountOpen, setAccountOpen] = useState(false)
    const [isAdmin, setIsAdmin] = useState(false)

    // ── Persist the footer collapsibles (Feedback / askFinz Portal / Account) ──
    // They previously used ephemeral useState(false), so every navigation (each
    // *.askfinz.ai app is a full page load) reset them to closed. Persist open-
    // state in the SAME shared cross-subdomain cookie NavMain uses
    // (AskFinz_sidebar_sections) and auto-open the section for the current app.
    const writeSection = (key: string, value: boolean) => {
        if (typeof document === "undefined") return
        const m = document.cookie.match(/(?:^|;\s*)AskFinz_sidebar_sections=([^;]*)/)
        let cur: Record<string, boolean> = {}
        if (m) { try { cur = JSON.parse(decodeURIComponent(m[1])) } catch { /* ignore */ } }
        cur[key] = value
        const domain = typeof window !== "undefined" && window.location.hostname.endsWith("askfinz.ai") ? "; Domain=.askfinz.ai" : ""
        document.cookie = `AskFinz_sidebar_sections=${encodeURIComponent(JSON.stringify(cur))}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax${domain}`
    }

    // SSR-safe: read the cookie on the client (never in the useState initializer —
    // that would cause hydration mismatch / React #418).
    useEffect(() => {
        if (typeof document === "undefined" || typeof window === "undefined") return
        const m = document.cookie.match(/(?:^|;\s*)AskFinz_sidebar_sections=([^;]*)/)
        let saved: Record<string, boolean> = {}
        if (m) { try { saved = JSON.parse(decodeURIComponent(m[1])) } catch { /* ignore */ } }
        const host = window.location.hostname
        if (saved["Feedback"] || host.startsWith("feedback.")) setFeedbackOpen(true)
        if (saved["askFinz Portal"] || host === "askfinz.ai" || host === "www.askfinz.ai") setPortalOpen(true)
        if (saved["Account"] || host.startsWith("account.")) setAccountOpen(true)
    }, [])

    useEffect(() => {
        const ADMIN_EMAILS = ["paul@pvarghese.com"]
        const checkAdmin = () => {
            const u = getSidebarUser()
            setIsAdmin(!!u?.email && ADMIN_EMAILS.includes(u.email.toLowerCase()))
        }
        checkAdmin()
        window.addEventListener("authUserUpdated", checkAdmin)
        window.addEventListener("storage", checkAdmin)
        window.addEventListener("focus", checkAdmin)
        const t = setTimeout(checkAdmin, 1000)
        return () => {
            window.removeEventListener("authUserUpdated", checkAdmin)
            window.removeEventListener("storage", checkAdmin)
            window.removeEventListener("focus", checkAdmin)
            clearTimeout(t)
        }
    }, [])

    // ── Cross-app sidebar scroll persistence ────────────────────────────
    // Each *.askfinz.ai app is a separate deployment, so navigating between
    // apps is a full page load and in-memory scroll resets. Persist the
    // sidebar's scrollTop to a Domain=.askfinz.ai cookie so the position is
    // preserved and in sync across every web app (and tab).
    const scrollRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const el = scrollRef.current
        if (!el) return
        const KEY = "AskFinz_sidebar_scroll"
        const readSaved = (): number => {
            if (typeof document === "undefined") return NaN
            const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${KEY}=([^;]*)`))
            return m ? parseInt(decodeURIComponent(m[1]), 10) : NaN
        }
        // Restore after first paint so content height is settled.
        const saved = readSaved()
        if (!Number.isNaN(saved)) {
            requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = saved })
        }
        // Save (rAF-debounced) on scroll, shared cross-subdomain.
        let raf = 0
        const onScroll = () => {
            if (raf) return
            raf = requestAnimationFrame(() => {
                raf = 0
                let domainAttr = ""
                if (typeof window !== "undefined" && window.location.hostname.endsWith("askfinz.ai")) {
                    domainAttr = "; Domain=.askfinz.ai"
                }
                document.cookie = `${KEY}=${Math.round(el.scrollTop)}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax${domainAttr}`
            })
        }
        el.addEventListener("scroll", onScroll, { passive: true })
        return () => {
            el.removeEventListener("scroll", onScroll)
            if (raf) cancelAnimationFrame(raf)
        }
    }, [])

    const handleSettingsClick = () => {
        if (onSettingsClick) {
            onSettingsClick()
        }
        setSettingsOpen(true)
    }

    return (
        <Sidebar collapsible="icon" className="hidden lg:block h-full" {...props}>
            <SidebarHeader className="!gap-0 !pb-2">
                <TeamSwitcher />
            </SidebarHeader>
            <SidebarContent className="flex flex-col min-h-0">
                {showSearch && (
                    <>
                        <SidebarGroup className="px-2 py-2 group-data-[collapsible=icon]:items-center">
                            <SearchDialog
                                items={searchItems}
                                placeholder={searchPlaceholder}
                                onSearch={onSearch}
                                showCategories={true}
                            />
                        </SidebarGroup>
                        <SidebarSeparator className="flex-shrink-0" />
                    </>
                )}
                <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
                    <NavMain items={navMain} onSettingsClick={handleSettingsClick} />
                    {navSecondary && navSecondary.length > 0 && (
                        <>
                            <SidebarSeparator className="flex-shrink-0" />
                            <div className="flex-shrink-0">
                                <NavMain items={navSecondary} onSettingsClick={handleSettingsClick} />
                            </div>
                        </>
                    )}
                    <SidebarSeparator className="flex-shrink-0" />
                    <SidebarGroup className="flex-shrink-0 !p-0.5">
                        <SidebarMenu className="mb-0 gap-0">
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    asChild
                                    tooltip="Dev Docs"
                                    className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center"
                                >
                                    <a
                                        href="https://docs.askfinz.ai/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex w-full items-center gap-1.5"
                                    >
                                        <BookOpen className="h-4 w-4 shrink-0" />
                                        <span>Dev Docs</span>
                                    </a>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                            <Collapsible
                                asChild
                                open={feedbackOpen}
                                onOpenChange={(o) => { setFeedbackOpen(o); writeSection("Feedback", o) }}
                                className="group/collapsible"
                            >
                                <SidebarMenuItem>
                                    <CollapsibleTrigger asChild>
                                        <SidebarMenuButton
                                            tooltip="Feedback"
                                            className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center"
                                        >
                                            <Bug className="h-4 w-4 shrink-0" />
                                            <span>Feedback</span>
                                            <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                                        </SidebarMenuButton>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <SidebarMenuSub>
                                            {FEEDBACK_ITEMS.map((item) => (
                                                <SidebarMenuSubItem key={item.title}>
                                                    <SidebarMenuSubButton asChild>
                                                        <a
                                                            href={`${FEEDBACK_BASE}${item.path}`}
                                                            className="flex items-center gap-1.5"
                                                        >
                                                            <item.icon className="size-3.5 shrink-0 text-sidebar-foreground/70" />
                                                            <span>{item.title}</span>
                                                        </a>
                                                    </SidebarMenuSubButton>
                                                </SidebarMenuSubItem>
                                            ))}
                                        </SidebarMenuSub>
                                    </CollapsibleContent>
                                </SidebarMenuItem>
                            </Collapsible>
                            {isAdmin && (
                            <Collapsible
                                asChild
                                open={portalOpen}
                                onOpenChange={(o) => { setPortalOpen(o); writeSection("askFinz Portal", o) }}
                                className="group/collapsible"
                            >
                                <SidebarMenuItem>
                                    <CollapsibleTrigger asChild>
                                        <SidebarMenuButton
                                            tooltip="askFinz Portal"
                                            className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center"
                                        >
                                            <Lock className="h-4 w-4 shrink-0" />
                                            <span>askFinz Portal</span>
                                            <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                                        </SidebarMenuButton>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <SidebarMenuSub>
                                            {PORTAL_ITEMS.map((item) => (
                                                <SidebarMenuSubItem key={item.title}>
                                                    <SidebarMenuSubButton asChild>
                                                        <a
                                                            href={`${PORTAL_BASE}${item.path}`}
                                                            className="flex items-center gap-1.5"
                                                        >
                                                            <item.icon className="size-3.5 shrink-0 text-sidebar-foreground/70" />
                                                            <span>{item.title}</span>
                                                        </a>
                                                    </SidebarMenuSubButton>
                                                </SidebarMenuSubItem>
                                            ))}
                                        </SidebarMenuSub>
                                    </CollapsibleContent>
                                </SidebarMenuItem>
                            </Collapsible>
                            )}
                            <Collapsible
                                asChild
                                open={accountOpen}
                                onOpenChange={(o) => { setAccountOpen(o); writeSection("Account", o) }}
                                className="group/collapsible"
                            >
                                <SidebarMenuItem>
                                    <CollapsibleTrigger asChild>
                                        <SidebarMenuButton
                                            tooltip="Account"
                                            className="group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:items-center"
                                        >
                                            <User className="h-4 w-4 shrink-0" />
                                            <span>Account</span>
                                            <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                                        </SidebarMenuButton>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <SidebarMenuSub>
                                            {ACCOUNT_ITEMS.map((item) => (
                                                <SidebarMenuSubItem key={item.title}>
                                                    <SidebarMenuSubButton asChild>
                                                        <a
                                                            href={`${getAccountAppUrl()}${item.path}`}
                                                            className="flex items-center gap-1.5"
                                                        >
                                                            <item.icon className="size-3.5 shrink-0 text-sidebar-foreground/70" />
                                                            <span>{item.title}</span>
                                                        </a>
                                                    </SidebarMenuSubButton>
                                                </SidebarMenuSubItem>
                                            ))}
                                        </SidebarMenuSub>
                                    </CollapsibleContent>
                                </SidebarMenuItem>
                            </Collapsible>
                        </SidebarMenu>
                    </SidebarGroup>
                </div>
            </SidebarContent>
            <SidebarFooter className="flex-shrink-0 !gap-0 !px-0 !pt-2 !pb-0">
                <NavUser user={user} />
            </SidebarFooter>
            <SidebarRail />
            {settingsDialog}
        </Sidebar>
    )
}

